<template>
  <v-card flat rounded="lg" class="gap-backtest-card">
    <v-card-title class="px-5 py-4">
      <span class="text-h6 font-weight-medium">{{ title }}</span>
    </v-card-title>
    <v-card-text class="px-5 pb-5 chart-box">
      <Line :data="chartData" :options="chartOptions" />
    </v-card-text>
  </v-card>
</template>

<script>
// Shared equity curve -- daily-bucketed P&L, cumulatively summed, as an
// honest visual approximation of the running equity curve (drawdown *stats*
// are computed server-side per-trade, more precise than this daily-bucketed
// chart, which is for shape, not for reading an exact figure off it). Used
// by Day Review's Analytics tab (real closed PaperTrade rows) and History
// tab (backtest-derived rows, via buildDashboardStats()'s `daily` field in
// day-review.controllers.js) so both read the same chart off the same
// `daily` bucket shape (bucketSummary(trades, 'day')) instead of two copies.
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

export default {
  name: 'EquityCurveChart',
  components: { Line },
  props: {
    daily: { type: Array, default: () => [] },
    title: { type: String, default: 'Cumulative P&L' },
  },
  data() {
    return {
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => ` ₹${Number(ctx.parsed.y).toLocaleString('en-IN')}` } },
        },
        scales: {
          x: { grid: { display: false }, ticks: { autoSkip: true, maxTicksLimit: 10 } },
          y: { grid: { color: '#e1e0d9' } },
        },
      },
    };
  },
  computed: {
    chartData() {
      let running = 0;
      const points = this.daily.map((d) => { running += Number(d.pnlRs) || 0; return running; });
      return {
        labels: this.daily.map((d) => d.period),
        datasets: [{
          label: 'Cumulative P&L (₹)',
          data: points,
          borderColor: '#2a78d6',
          backgroundColor: '#2a78d6',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 5,
          tension: 0.15,
        }],
      };
    },
  },
};
</script>

<style scoped>
/* Chart.js (maintainAspectRatio: false) needs an explicitly-sized,
   positioned container to render into -- DayReview.vue's own .chart-box
   rule is scoped to that file only, so this component needs its own copy. */
.chart-box {
  height: 320px;
  position: relative;
}
</style>
